'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { SortOrder } from '@dealroadshow/uikit-table/DataTable/interfaces/SortOrder';
import DataroomListingRepository from '../infrastructure/DataroomListingRepository';
import useFetchCollection from '@framework/state/useFetchCollection';
import SectorContext, { useSectorContext } from '@dm-portal/sector/SectorContext';
import IndustryContext, { useIndustryContext } from './IndustryContext';
import TransactionTypeContext, { useTransactionTypeContext } from './TransactionTypeContext';
import { useDataroomTenantContext } from '@dataroom/tenant/DataroomTenantContext';
import { IDataroomListItem } from '../domain/vo/DataroomListItem';
import config from '@dataroom/config';
import DataroomErrorHandler from '@dataroom/error-handler';
import { getContainer } from '@framework/di/getContainer';
import useHideDeactivated from './useHideDeactivated';

const sortBy: string = 'createdAt';
const sortOrder: SortOrder = 'desc';

const useDataroomListing = () => {
  const container = getContainer();

  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const dataroomRepository: DataroomListingRepository = container.get(DataroomListingRepository);
  const { tenant } = useDataroomTenantContext();

  const { getSectorList } = useSectorContext();
  const { getIndustryList } = useIndustryContext();
  const { getTransactionTypes } = useTransactionTypeContext();
  const {
    isHideDeactivated,
    isHideDeactivatedRestored,
    setIsHideDeactivated,
  } = useHideDeactivated();

  useEffect(() => {
    if (
      tenant === config.investorset.code ||
      tenant === config.verisend.code ||
      tenant === config.researchroom.code
    ) {
      Promise.all([getSectorList(), getIndustryList()])
        .then(() => {
          setIsInitialized(true);
        });
    } else if (tenant === config.loansend.code) {
      getTransactionTypes()
        .then(() => {
          setIsInitialized(true);
        });
    } else {
      setIsInitialized(true);
    }
  }, []);

  const fetchCollection = useFetchCollection<IDataroomListItem>(
    dataroomRepository.getList,
    {
      enabled: isInitialized && isHideDeactivatedRestored,
      payload: isHideDeactivated ? { excludeDeactivated: true } : undefined,
      isFiltered: isHideDeactivated,
      initialValues: {
        sortBy,
        sortOrder,
      },
      onError: container.get(DataroomErrorHandler).handleError,
    },
  );

  // TODO: @vladimirlomonos Looks like the problem is somewhere deeper in the connection between routing and contexts.
  // useFetchCollection hook was made in order not to think about when to call fetch.
  // Just to avoid blocking of SSR, we gonna keep it for a while
  useEffect(() => {
    fetchCollection.isInitialized && fetchCollection.getCollection();
  }, [tenant]);

  const toggleHideDeactivated = (): void => {
    setIsHideDeactivated(!isHideDeactivated);
    fetchCollection.paginate(1);
  };

  return {
    ...fetchCollection,
    isFetching: fetchCollection.isFetching || !isInitialized || !isHideDeactivatedRestored,
    isHideDeactivated,
    toggleHideDeactivated,
  };
};

type DataroomListingContextType = ReturnType<typeof useDataroomListing>;

export const DataroomListingContext = createContext<DataroomListingContextType | null>(null);

export const useDataroomListingContext = () => {
  const context = useContext(DataroomListingContext);
  if (!context) {
    throw new Error('useDataroomListingContext must be used within a DataroomListingContextProvider');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
}

const DataroomListingContextProvider = ({ children }: IProps) => (
  <DataroomListingContext.Provider value={ useDataroomListing() }>
    { children }
  </DataroomListingContext.Provider>
);

export default ({ children }: IProps) => (
  <SectorContext>
    <IndustryContext>
      <TransactionTypeContext>
        <DataroomListingContextProvider>
          { children }
        </DataroomListingContextProvider>
      </TransactionTypeContext>
    </IndustryContext>
  </SectorContext>
);
